import { Box, Spinner, Table } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import { getFilterValue } from 'modules/core/components/EmployeeMultiFilters/commonFilters'
import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import employeeStatuses from 'modules/core/components/EmployeeMultiFilters/hooks/utils/employeeStatuses'
import createComputed from 'modules/core/hooks/createComputed'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import SearchBar from 'modules/payroll_bundle/components/common/components/_SearchBar'
import {
  PersistedResult,
  useGetPersistedResults,
} from 'modules/payroll_bundle/hooks/useGetPersistedResults'
import { ImportData } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { ERROR, NEW, UNCHANGED, UPDATED } from 'modules/payroll_bundle/lib/constants'
import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'
import { useEmployeeSearchFilter } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/useEmployeeSearchFilter'

import { getUserFromImportData } from './RowValidations'
import Header from './_Header'
import ResultsGroups from './_ResultsGroups'
import { getResultFileStatus } from './getResultFileStatus'
import { GroupProp } from './types'

interface Props {
  onSelectAll: () => void
  onSelectItem: (resultItem: ImportData) => void
  selectable: Array<number | null>
  selected?: Set<number>
  checkable?: boolean
  loading: boolean
  onUnAssignEmployee: (userId: number, index: number) => void
  onAssignEmployee: (userId: number, index: number) => void
  currency: string
  importedEmployees: ImportData[]
  payrollPolicyPeriodId: number
  compensationType: string
}

const FILTER_KEY = LOCAL_STORAGE_KEYS.payrollResultsFilters

const useGroupResultItems = createComputed(
  (itemsToShow: ImportData[], persistedItems: ImportData[]) => {
    const errorRows = i18n.t('payroll_bundle.payroll_results.review_and_import.error_rows')
    const updateRows = i18n.t('payroll_bundle.payroll_results.review_and_import.updated_rows')
    const newRows = i18n.t('payroll_bundle.payroll_results.review_and_import.ready_to_import')
    const unchangedRows = i18n.t('payroll_bundle.payroll_results.review_and_import.unchanged_row')

    const defaultGroupResultItems: GroupProp[] = [
      {
        name: errorRows,
        count: 0,
        status: ERROR,
        resultItems: [],
      },
      {
        name: newRows,
        count: 0,
        status: NEW,
        resultItems: [],
      },
      {
        name: updateRows,
        count: 0,
        status: UPDATED,
        resultItems: [],
      },
      {
        name: unchangedRows,
        count: 0,
        status: UNCHANGED,
        resultItems: [],
      },
    ]

    const indexedPersistedItems = persistedItems.reduce((acc: Record<number, ImportData>, item) => {
      acc[item.employee_id || 0] = item
      return acc
    }, {})

    return itemsToShow.reduce((acc: GroupProp[], resultItem: ImportData) => {
      const resultStatus = getResultFileStatus(
        resultItem,
        indexedPersistedItems[resultItem.employee_id ?? 0]
      )

      if (resultStatus === UPDATED && indexedPersistedItems[resultItem.employee_id ?? 0]) {
        const currentResultItem = indexedPersistedItems[resultItem.employee_id ?? 0]
        resultItem.current_company_costs = currentResultItem?.company_costs
        resultItem.current_gross_salary = currentResultItem?.gross_salary
        resultItem.current_net_salary = currentResultItem?.net_salary
      }

      const data = acc.find((item) => item.status === resultStatus)

      // FIXME: This is mutating a value inside acc and also not checking undefined values.
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      data!.count++
      data?.resultItems.push(resultItem)

      // Sort resultItems array within each group (minus Error) by assigned_date
      data?.resultItems.sort((a, b) => {
        const aDate = a?.assigned_date ?? 0
        const bDate = b?.assigned_date ?? 0

        if (aDate && !bDate) {
          return -1
        } else if (!aDate && bDate) {
          return 1
        }

        return bDate - aDate
      })

      return acc
    }, defaultGroupResultItems)
  }
)

const usePersistedResultsToImportData = createComputed(
  (persistedResults: PersistedResult[], resultConceptType: string) => {
    const filteredResults = persistedResults.filter((result) => {
      return result.payType === resultConceptType
    })

    const importData: ImportData[] = filteredResults.map((result) => {
      const data: ImportData = {
        id: result.employee.id,
        employee_id: result.employee.id,
        net_salary: null,
        gross_salary: null,
        company_costs: null,
        last_name: result.employee.lastName,
        first_name: result.employee.firstName,
        full_name: result.employee.fullName,
        company_identifier: null,
        match_reason: null,
        concepts: [], // We don't need them
      }

      result.resultConceptItems.forEach((conceptItem) => {
        switch (conceptItem.concept.name) {
          case DEFAULT_RESULT_CONCEPT.net_pay:
            data.net_salary = conceptItem.amount || null
            break
          case DEFAULT_RESULT_CONCEPT.gross_pay:
            data.gross_salary = conceptItem.amount || null
            break
          case DEFAULT_RESULT_CONCEPT.company_cost:
            data.company_costs = conceptItem.amount || null
            break
        }
      })

      return data
    })

    return importData
  }
)

const ResultsTable = ({
  checkable,
  selected,
  selectable,
  loading,
  onSelectAll,
  onSelectItem,
  onAssignEmployee,
  onUnAssignEmployee,
  currency,
  importedEmployees,
  payrollPolicyPeriodId,
  compensationType,
}: Props) => {
  const DEFAULT_FILTER_VALUES = [
    {
      field: 'employee_status',
      value: employeeStatuses.active,
    },
    {
      field: 'employee_status',
      value: employeeStatuses.terminated,
    },
  ]

  const EMPLOYEE_FILTER_OPTIONS = {
    skipDefaultFilters: DEFAULT_FILTER_VALUES,
  }

  const { data: persistedResults, loading: persistedResultsLoading } =
    useGetPersistedResults(payrollPolicyPeriodId)

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const {
    filteredModels: filteredEmployees,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, FILTER_KEY, EMPLOYEE_FILTER_OPTIONS)

  useUnsafeEffect(() => {
    onApplyFilters(employees.models)
  }, [employees]) // eslint-disable-line react-hooks/exhaustive-deps

  const itemsToShow = useEmployeeSearchFilter(
    debouncedSearch,
    filteredEmployees,
    importedEmployees
  ).get()
  const persistedImportedData = usePersistedResultsToImportData(
    persistedResults,
    compensationType
  ).get()

  const groupResultItemsToShow = useGroupResultItems(itemsToShow, persistedImportedData).get()

  const Options = () => (
    <Box
      width="full"
      alignItems="center"
      flexDirection="row"
      background="grey200"
      justifyContent="spaceBetween"
    >
      <SearchBar
        search={search}
        onSearch={onSearch}
        onCancel={onCancel}
        filters={filters}
        onAddFilter={onAddFilter}
        logicOperator={logicOperator}
        onRemoveFilter={onRemoveFilter}
        onApplyFilters={() => onApplyFilters(employees.models)}
        hideFilters
      />
    </Box>
  )

  return (
    <Table autoLayout fullWidth options={Options}>
      <Table noRadius hideBorder>
        <Header
          checkable={checkable}
          selected={selected}
          selectable={selectable}
          onSelectAll={onSelectAll}
        />
      </Table>
      <Table noRadius hideBorder>
        {loading || persistedResultsLoading ? (
          <Box alignItems="center" padding="s24" background="white">
            <Spinner />
          </Box>
        ) : (
          <ResultsGroups
            checkable={checkable}
            selected={selected}
            onUnAssignEmployee={onUnAssignEmployee}
            onAssignEmployee={onAssignEmployee}
            groups={groupResultItemsToShow}
            getUserFromImportData={getUserFromImportData}
            onSelectItem={onSelectItem}
            currency={currency}
          />
        )}
      </Table>
    </Table>
  )
}

export default observer(ResultsTable)
